// ---------------------------------------------------------------------------
// Help content available at any time (the "?" icon in the top bar, see
// HelpPanel.tsx) — a summary of what the app can do, not the technical
// documentation in CLAUDE.md. Each entry is independent and searchable;
// `keywords` are terms a user might type that don't literally appear in the
// title or content (synonyms, file names, shortcuts...).
//
// The content itself lives in help.es.ts/help.en.ts (one file per language,
// same `id` keys in both — see test/help.test.ts, which checks that none
// is left orphaned in one language) — this file only defines the shared
// shape (HelpEntry) and the language-agnostic search functions.
// ---------------------------------------------------------------------------
import { HELP_EN, CATEGORIES_EN } from "./help.en.ts";
import { HELP_ES, CATEGORIES_ES } from "./help.es.ts";
import type { Language } from "../model/language.ts";

export interface HelpEntry {
  id: string;
  category: string;
  title: string;
  keywords: string[];
  content: string[];
}

export const HELP_BY_LANGUAGE: Record<Language, HelpEntry[]> = {
  es: HELP_ES,
  en: HELP_EN,
};

export const CATEGORIES_BY_LANGUAGE: Record<Language, readonly string[]> = {
  es: CATEGORIES_ES,
  en: CATEGORIES_EN,
};

function normalize(text: string): string {
  return text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/** Filters help entries by a free-text search (every word must appear, in any order, ignoring case/accents). Empty query = all entries. */
export function searchHelp(entries: HelpEntry[], query: string): HelpEntry[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return entries;
  return entries.filter((e) => {
    const text = normalize([e.title, e.category, ...e.keywords, ...e.content].join(" "));
    return words.every((w) => text.includes(w));
  });
}
